/**
 * TenantDetailPage — Task 25.2 (DEC-113). PLATFORM_ADMIN tenant detail.
 *
 * Header (name + abbreviation + status + Edit / Suspend|Reactivate), inline-editable
 * Company Info (with a live HaulId preview), Setup Wizard status (progress + per-step),
 * read-only Operators, Activity (drivers / 30-day jobs / last completed), and Features:
 * hauler feature switches that Paelode controls (DEC-351 addendum, migration 0192).
 */
import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBackTo } from '../../lib/url-state';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import {
  FormRow,
  fieldStyle,
  labelStyle,
  subText,
  Pill,
  StatusBadge,
  haulIdPreview,
} from '../../ui/bits';
import {
  useTenant,
  useUpdateTenant,
  useSuspendTenant,
  useReactivateTenant,
  type TenantSummary,
} from './hooks';
import { PlanFeaturesTab } from './PlanFeaturesTab';
import { TenantBillingCard } from '../billing/TenantBillingCard';
import { TAB_TRACK_STYLE, tabPillStyle } from 'src/components/ui/tab-styles';
import { ABBREVIATION_RE, ABBREVIATION_RULE_TEXT } from '@lodex-pro/shared/abbreviation';
import { formatNumber } from '@lodex-pro/shared/number-format';

// §58 PR 2 — Overview (the cards below) and Plan & Features.
type DetailTab = 'overview' | 'features';
const DETAIL_TABS: ReadonlyArray<{ id: DetailTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'features', label: 'Plan & Features' },
];

const tabStyle = tabPillStyle;

function fmtDate(iso: string | null): string {
  if (iso === null) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const SECTION_LABELS = [
  'Company',
  'Operators & Customers',
  'Disposal Wells',
  'Yards',
  'Trucks & Trailers',
  'Drivers',
  'Job Types & Rates',
] as const;

function Card({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        borderRadius: 'var(--radius-card)',
        padding: '20px',
        marginBottom: '16px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '14px',
        }}
      >
        <h3 style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function ReadField({ label, value }: { label: string; value: string | null }): React.JSX.Element {
  return (
    <div>
      <div style={labelStyle}>{label}</div>
      <div style={{ fontSize: '14px', color: 'var(--color-text-primary)' }}>
        {value !== null && value !== '' ? value : <span style={subText}>—</span>}
      </div>
    </div>
  );
}

function CompanyInfo({ tenant }: { tenant: TenantSummary }): React.JSX.Element {
  const [editing, setEditing] = React.useState(false);
  const [name, setName] = React.useState(tenant.name);
  const [abbrev, setAbbrev] = React.useState(tenant.haulerAbbreviation ?? '');
  const [dot, setDot] = React.useState(tenant.dotNumber ?? '');
  const [phone, setPhone] = React.useState(tenant.primaryPhone ?? '');
  const [email, setEmail] = React.useState(tenant.primaryEmail ?? '');
  const [address, setAddress] = React.useState(tenant.businessAddress ?? '');
  const [error, setError] = React.useState<string | null>(null);

  const update = useUpdateTenant(tenant.id);

  const start = (): void => {
    setName(tenant.name);
    setAbbrev(tenant.haulerAbbreviation ?? '');
    setDot(tenant.dotNumber ?? '');
    setPhone(tenant.primaryPhone ?? '');
    setEmail(tenant.primaryEmail ?? '');
    setAddress(tenant.businessAddress ?? '');
    setError(null);
    setEditing(true);
  };

  const save = (): void => {
    if (name.trim() === '') {
      setError('Name is required.');
      return;
    }
    if (!ABBREVIATION_RE.test(abbrev.trim())) {
      setError(ABBREVIATION_RULE_TEXT);
      return;
    }
    update.mutate(
      {
        name: name.trim(),
        haulerAbbreviation: abbrev.trim().toUpperCase(),
        dotNumber: dot.trim() === '' ? null : dot.trim(),
        primaryPhone: phone.trim() === '' ? null : phone.trim(),
        primaryEmail: email.trim() === '' ? null : email.trim(),
        businessAddress: address.trim() === '' ? null : address.trim(),
      },
      {
        onSuccess: () => {
          toast.success('Tenant updated');
          setEditing(false);
        },
        onError: () =>
          setError('Could not save — the abbreviation may already be taken by another tenant.'),
      },
    );
  };

  if (!editing) {
    return (
      <Card
        title="Company Info"
        action={
          <Button type="button" variant="outline" onClick={start}>
            Edit
          </Button>
        }
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <ReadField label="Name" value={tenant.name} />
          <ReadField label="Hauler abbreviation" value={tenant.haulerAbbreviation} />
          <ReadField label="DOT number" value={tenant.dotNumber} />
          <ReadField label="Primary phone" value={tenant.primaryPhone} />
          <ReadField label="Primary email" value={tenant.primaryEmail} />
          <ReadField label="Business address" value={tenant.businessAddress} />
        </div>
        <p style={{ ...subText, marginTop: '14px', textTransform: 'none' }}>
          Ticket IDs: {haulIdPreview(tenant.haulerAbbreviation ?? '')}
        </p>
      </Card>
    );
  }

  return (
    <Card title="Company Info">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
        <FormRow label="Name" htmlFor="e-name" required>
          <input
            id="e-name"
            value={name}
            onChange={(e): void => setName(e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
        <FormRow
          label="Hauler abbreviation"
          htmlFor="e-abbrev"
          required
          hint={`Ticket IDs: ${haulIdPreview(abbrev)}`}
        >
          <input
            id="e-abbrev"
            value={abbrev}
            maxLength={4}
            onChange={(e): void => setAbbrev(e.target.value.toUpperCase())}
            style={{ ...fieldStyle, textTransform: 'uppercase' }}
          />
        </FormRow>
        <FormRow label="DOT number" htmlFor="e-dot">
          <input
            id="e-dot"
            value={dot}
            onChange={(e): void => setDot(e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
        <FormRow label="Primary phone" htmlFor="e-phone">
          <input
            id="e-phone"
            value={phone}
            onChange={(e): void => setPhone(e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
        <FormRow label="Primary email" htmlFor="e-email">
          <input
            id="e-email"
            type="email"
            value={email}
            onChange={(e): void => setEmail(e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
        <FormRow label="Business address" htmlFor="e-address">
          <input
            id="e-address"
            value={address}
            onChange={(e): void => setAddress(e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
      </div>
      {error !== null && (
        <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)', marginTop: '12px' }}>
          {error}
        </p>
      )}
      <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
        <Button
          type="button"
          disabled={update.isPending}
          onClick={save}
          style={{ backgroundColor: 'var(--color-accent-blue)', color: 'var(--color-on-accent)' }}
        >
          {update.isPending ? 'Saving…' : 'Save changes'}
        </Button>
        <Button type="button" variant="outline" onClick={(): void => setEditing(false)}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}

export function TenantDetailPage(): React.JSX.Element {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  // Back to the list as it was left: its search and filters (lib/url-state.ts).
  const backTo = useBackTo('/tenants');
  const { data, isLoading, isError } = useTenant(id);
  const suspend = useSuspendTenant(id);
  const reactivate = useReactivateTenant(id);
  const [tab, setTab] = React.useState<DetailTab>('overview');

  if (isLoading) return <p style={{ ...subText, padding: '24px' }}>Loading tenant…</p>;
  if (isError || data === undefined) {
    return (
      <div style={{ padding: '24px' }}>
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Could not load this tenant.
        </p>
        <Button type="button" variant="outline" onClick={(): void => void navigate(backTo)}>
          Back to tenants
        </Button>
      </div>
    );
  }

  const { tenant, operators, setupWizard, activity } = data;

  const onSuspend = (): void => {
    if (!window.confirm(`Suspend ${tenant.name}? Its users will be blocked from signing in.`)) {
      return;
    }
    suspend.mutate(
      {},
      {
        onSuccess: () => toast.success('Tenant suspended'),
        onError: () => toast.error('Could not suspend tenant'),
      },
    );
  };
  const onReactivate = (): void => {
    reactivate.mutate(
      {},
      {
        onSuccess: () => toast.success('Tenant reactivated'),
        onError: () => toast.error('Could not reactivate tenant'),
      },
    );
  };

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
      <button
        type="button"
        onClick={(): void => void navigate(backTo)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          background: 'none',
          border: 'none',
          color: 'var(--color-text-secondary)',
          cursor: 'pointer',
          fontSize: '13px',
          marginBottom: '12px',
          padding: 0,
        }}
      >
        <ArrowLeft size={16} aria-hidden="true" /> Tenants
      </button>

      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '20px',
          gap: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>{tenant.name}</h2>
          {tenant.haulerAbbreviation !== null && (
            <Pill label={tenant.haulerAbbreviation} tone="accent" />
          )}
          <StatusBadge active={tenant.isActive} />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {tenant.isActive ? (
            <Button
              type="button"
              variant="outline"
              disabled={suspend.isPending}
              onClick={onSuspend}
              style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
            >
              Suspend
            </Button>
          ) : (
            <Button
              type="button"
              disabled={reactivate.isPending}
              onClick={onReactivate}
              style={{
                backgroundColor: 'var(--color-accent-blue)',
                color: 'var(--color-on-accent)',
              }}
            >
              Reactivate
            </Button>
          )}
        </div>
      </div>

      <div role="tablist" style={{ ...TAB_TRACK_STYLE, marginBottom: '16px' }}>
        {DETAIL_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={(): void => setTab(t.id)}
            style={tabStyle(tab === t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'features' ? (
        <PlanFeaturesTab tenantId={tenant.id} />
      ) : (
        <>
          <CompanyInfo tenant={tenant} />

          {/* Phase 21 / Task 21.7a — feature plan and billing plan side by side. */}
          <TenantBillingCard tenantId={tenant.id} tenantType={tenant.tenantType} />

          {/* Setup Wizard */}
          <Card title="Setup Wizard Status">
            <div
              style={{
                height: '8px',
                borderRadius: '4px',
                backgroundColor: 'var(--color-bg-tertiary)',
                overflow: 'hidden',
                marginBottom: '6px',
              }}
            >
              <div
                style={{
                  width: `${setupWizard.completionPct}%`,
                  height: '100%',
                  backgroundColor: 'var(--color-accent-blue)',
                }}
              />
            </div>
            <p style={{ ...subText, marginBottom: '14px', textTransform: 'none' }}>
              {setupWizard.completionPct}% complete
              {setupWizard.allComplete
                ? ` · launched ${fmtDate(setupWizard.completedAt)}`
                : ` · on step ${setupWizard.lastSection} of 7`}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {setupWizard.sections.map((s) => (
                <div
                  key={s.section}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: '16px',
                      color: s.effectiveComplete
                        ? 'var(--color-success)'
                        : 'var(--color-text-tertiary)',
                    }}
                  >
                    {s.effectiveComplete ? '✓' : '○'}
                  </span>
                  <span style={{ color: 'var(--color-text-primary)' }}>
                    {s.section}. {SECTION_LABELS[s.section - 1]}
                  </span>
                  {s.adminOverride && <Pill label="Admin override" tone="muted" />}
                </div>
              ))}
            </div>
          </Card>

          {/* Operators */}
          <Card title={`Operators (${operators.length})`}>
            {operators.length === 0 ? (
              <p style={{ ...subText, textTransform: 'none' }}>No operators configured yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {operators.map((o) => (
                  <div
                    key={o.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '8px 0',
                      borderBottom: '1px solid var(--color-border-secondary)',
                      fontSize: '14px',
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>{o.name}</span>
                    <Pill label={o.abbreviation} tone="neutral" />
                    {/* 0205 — the code its tickets actually print, when it is not the stored one. */}
                    {o.ticketCode !== undefined && o.ticketCode !== o.abbreviation && (
                      <Pill label={`Tickets: ${o.ticketCode}`} tone="accent" />
                    )}
                    {/* DEC-312 — "Platform", not DEC-109's "lodeX Pro". The pill marks
                    provenance: a platform-catalog operator vs a hauler-private
                    customer. DEC-109 chose the product brand for the identical pill in
                    web-dispatch (OperatorsTab.tsx), which is hauler-facing and correct
                    there; this console is Paelode-internal (docs/CLAUDE.md:61), so it
                    uses the neutral word it already uses for "Platform Changes" and
                    "Platform Admin". Corbin scoped the change to web-admin only, so the
                    two pills deliberately differ. */}
                    <Pill label={o.isPlatformOperator ? 'Platform' : 'Customer'} tone="accent" />
                    {!o.isActive && <Pill label="Inactive" tone="muted" />}
                  </div>
                ))}
              </div>
            )}
            <p style={{ ...subText, marginTop: '10px', textTransform: 'none' }}>
              Read-only. Operators are managed by the hauler.
            </p>
          </Card>

          {/* Activity */}
          <Card title="Activity">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '24px', fontWeight: 700 }}>
                  {formatNumber(activity.driverCount)}
                </div>
                <div style={subText}>Active drivers</div>
              </div>
              <div>
                <div style={{ fontSize: '24px', fontWeight: 700 }}>
                  {formatNumber(activity.jobCount30d)}
                </div>
                <div style={subText}>Jobs (last 30 days)</div>
              </div>
              <div>
                <div style={{ fontSize: '16px', fontWeight: 600, marginTop: '6px' }}>
                  {fmtDate(activity.lastJobCompletedAt)}
                </div>
                <div style={subText}>Last job completed</div>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
